import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Privacy Policy
            </h1>
            <div className="text-slate-400 space-y-1">
              <p><strong className="text-slate-300">Effective Date:</strong> January 13, 2026</p>
              <p><strong className="text-slate-300">Last Updated:</strong> January 13, 2026</p>
            </div>
          </div>

          {/* Introduction */}
          <div className="prose prose-invert prose-slate max-w-none">
            <p className="text-slate-300 text-lg leading-relaxed mb-8">
              This Privacy Policy explains how LoadSurf AI, Inc. ("LoadSurf AI," "we," "us," or "our") collects, uses, shares, and protects information when you access or use our website at www.LoadSurf.ai, our applications, software, APIs, integrations, and any related products or services (collectively, the "Services").
            </p>
            <p className="text-slate-300 text-lg leading-relaxed mb-12">
              By accessing or using the Services, you acknowledge that you have read and understood this Privacy Policy.
            </p>

            {/* Section 1 */}
            <Section number="1" title="Scope of This Policy">
              <p>This Privacy Policy applies to:</p>
              <ul>
                <li>Visitors to our website</li>
                <li>Users who create accounts or connect third-party platforms</li>
                <li>Customers using LoadSurf AI to aggregate or manage private load boards</li>
                <li>Data processed in connection with integrations and automation tools</li>
              </ul>
              <p>
                This policy does not apply to third-party services you access independently of LoadSurf AI. Those services are governed by their own privacy policies and terms.
              </p>
            </Section>

            {/* Section 2 */}
            <Section number="2" title="Information We Collect">
              <SubSection number="2.1" title="Information You Provide Directly">
                <p>We may collect information you voluntarily provide, including:</p>
                <ul>
                  <li>Name, email address, phone number</li>
                  <li>Company name, role, and business contact details</li>
                  <li>Account credentials and authentication information</li>
                  <li>Billing and payment information (processed by third-party providers)</li>
                  <li>Communications with us, including support requests and feedback</li>
                </ul>
              </SubSection>

              <SubSection number="2.2" title="Information from Connected or Linked Accounts">
                <p>
                  When you create an account with LoadSurf AI or use related services, we may automatically associate or link your account with other connected products or platforms when the same email address is used across services.
                </p>
                <p>If accounts are linked, we may collect and process information such as:</p>
                <ul>
                  <li>Account identifiers and authentication status</li>
                  <li>Integration and connection metadata</li>
                  <li>Load board access data, listings, pricing, and availability</li>
                  <li>Operational and configuration data required to provide integrated functionality</li>
                </ul>
                <p>
                  This account linkage enables cross-product features such as data synchronization, workflow automation, and unified account management.
                </p>
              </SubSection>

              <SubSection number="2.3" title="Automatically Collected Information">
                <p>We may automatically collect certain technical and usage information, including:</p>
                <ul>
                  <li>IP address, browser type, device identifiers</li>
                  <li>Usage logs, timestamps, and interaction metrics</li>
                  <li>Diagnostic and performance data</li>
                  <li>Cookies and similar tracking technologies (see Section 9)</li>
                </ul>
              </SubSection>
            </Section>

            {/* Section 3 */}
            <Section number="3" title="How We Use Information">
              <p>We use information to:</p>
              <ul>
                <li>Provide, operate, and maintain the Services</li>
                <li>Authenticate users and manage accounts</li>
                <li>Enable integrations and linked workflows</li>
                <li>Automatically associate accounts across connected services</li>
                <li>Improve functionality, performance, and reliability</li>
                <li>Maintain security and prevent fraud or misuse</li>
                <li>Process payments and manage subscriptions</li>
                <li>Communicate service updates, notices, and support responses</li>
                <li>Comply with legal and regulatory obligations</li>
              </ul>
              <p className="font-semibold text-amber-400">We do not sell personal information.</p>
            </Section>

            {/* Section 4 */}
            <Section number="4" title="Legal Bases for Processing (Where Applicable)">
              <p>Where required by law, we process information based on:</p>
              <ul>
                <li>Performance of a contract (providing the Services)</li>
                <li>Legitimate business interests (security, product improvement, integration)</li>
                <li>Compliance with legal obligations</li>
                <li>Your consent, where applicable</li>
              </ul>
            </Section>

            {/* Section 5 */}
            <Section number="5" title="How We Share Information">
              <SubSection number="5.1" title="Service Providers">
                <p>
                  We may share information with trusted service providers that help us operate the Services, including providers of:
                </p>
                <ul>
                  <li>Cloud infrastructure and hosting</li>
                  <li>Payment processing</li>
                  <li>Analytics, monitoring, and logging</li>
                  <li>Customer support and communications</li>
                </ul>
                <p>
                  These providers are contractually required to use information only as necessary to perform services on our behalf and to protect it appropriately.
                </p>
              </SubSection>

              <SubSection number="5.2" title="Linked Services & Integration Partners">
                <p>
                  When you use LoadSurf AI together with other connected products or platforms (such as dispatch or automation tools), information may be shared automatically when accounts are associated based on a matching email address.
                </p>
                <p>This sharing is performed to:</p>
                <ul>
                  <li>Enable integrated features and workflows</li>
                  <li>Maintain consistent account state across services</li>
                  <li>Reduce duplicate configuration and setup</li>
                  <li>Support automation and synchronization between systems</li>
                </ul>
                <p>
                  By creating or using accounts with the same email address across connected services, you acknowledge and agree that relevant information may be shared between those services as part of their intended operation.
                </p>
              </SubSection>

              <SubSection number="5.3" title="Account Linking & Automatic Data Sharing">
                <p>
                  LoadSurf AI may automatically link user accounts across connected services when the same email address is used. Once linked, information may be shared between those services without requiring additional user action each time.
                </p>
                <p>This automatic sharing is designed to:</p>
                <ul>
                  <li>Improve usability and efficiency</li>
                  <li>Enable seamless cross-product functionality</li>
                  <li>Reduce redundant data entry and configuration</li>
                </ul>
                <p>
                  You may request to disconnect linked accounts or limit data sharing by contacting{' '}
                  <a href="mailto:privacy@loadsurf.ai" className="text-sky-400 hover:text-sky-300">
                    privacy@loadsurf.ai
                  </a>
                  , subject to technical, contractual, or operational limitations.
                </p>
              </SubSection>

              <SubSection number="5.4" title="Legal & Compliance">
                <p>We may disclose information where required to:</p>
                <ul>
                  <li>Comply with applicable laws, regulations, or legal processes</li>
                  <li>Respond to lawful requests by public authorities</li>
                  <li>Enforce our Terms & Conditions</li>
                  <li>Protect the rights, safety, and property of LoadSurf AI, our users, or others</li>
                </ul>
              </SubSection>

              <SubSection number="5.5" title="Business Transfers">
                <p>
                  If LoadSurf AI is involved in a merger, acquisition, financing, reorganization, or sale of assets, information may be transferred as part of that transaction.
                </p>
              </SubSection>
            </Section>

            {/* Section 6 */}
            <Section number="6" title="Data Retention">
              <p>We retain information only as long as reasonably necessary to:</p>
              <ul>
                <li>Provide and maintain the Services</li>
                <li>Comply with legal, accounting, or reporting obligations</li>
                <li>Resolve disputes and enforce agreements</li>
              </ul>
              <p>When data is no longer required, it is securely deleted or anonymized.</p>
            </Section>

            {/* Section 7 */}
            <Section number="7" title="Data Security">
              <p>
                We implement reasonable administrative, technical, and organizational safeguards designed to protect information, including:
              </p>
              <ul>
                <li>Role-based access controls</li>
                <li>Encryption in transit where appropriate</li>
                <li>Monitoring and logging for suspicious activity</li>
                <li>Limited internal access based on business need</li>
              </ul>
              <p>
                No system is completely secure. You acknowledge that use of the Services involves inherent risk.
              </p>
            </Section>

            {/* Section 8 */}
            <Section number="8" title="Your Rights & Choices">
              <p>Depending on your jurisdiction, you may have the right to:</p>
              <ul>
                <li>Access information we hold about you</li>
                <li>Request correction or deletion</li>
                <li>Object to or restrict certain processing</li>
                <li>Withdraw consent where applicable</li>
              </ul>
              <p>
                Requests may be submitted to{' '}
                <a href="mailto:info@loadsurf.ai" className="text-sky-400 hover:text-sky-300">
                  info@loadsurf.ai
                </a>
                . We may require verification before fulfilling requests.
              </p>
            </Section>

            {/* Section 9 */}
            <Section number="9" title="Cookies & Tracking Technologies">
              <p>We use cookies and similar technologies to:</p>
              <ul>
                <li>Enable core website functionality</li>
                <li>Analyze usage and performance</li>
                <li>Improve user experience</li>
              </ul>
              <p>
                You can control cookies through your browser settings. Disabling cookies may affect functionality.
              </p>
            </Section>

            {/* Section 10 */}
            <Section number="10" title="California Privacy Rights (CCPA / CPRA)">
              <p>If you are a California resident, you have the right to:</p>
              <ul>
                <li>Know what personal information we collect and how it is used</li>
                <li>Request deletion of personal information</li>
                <li>Opt out of certain data sharing (we do not sell personal data)</li>
                <li>Not be discriminated against for exercising your rights</li>
              </ul>
              <p>
                Requests may be made by emailing{' '}
                <a href="mailto:info@loadsurf.ai" className="text-sky-400 hover:text-sky-300">
                  info@loadsurf.ai
                </a>
                .
              </p>
            </Section>

            {/* Section 11 */}
            <Section number="11" title="International Users">
              <p>
                The Services are operated from the United States. If you access the Services from outside the U.S., you consent to the transfer and processing of information in the United States.
              </p>
            </Section>

            {/* Section 12 */}
            <Section number="12" title="Children's Privacy">
              <p>
                The Services are intended for business use and are not directed to children under the age of 13. We do not knowingly collect personal information from children.
              </p>
            </Section>

            {/* Section 13 */}
            <Section number="13" title="Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. Material changes will be posted on the Site with an updated "Last Updated" date. Continued use of the Services constitutes acceptance of the updated policy.
              </p>
            </Section>

            {/* Section 14 */}
            <Section number="14" title="Contact Us">
              <p>For privacy-related questions or requests, contact:</p>
              <p className="mt-4">
                <a 
                  href="mailto:info@loadsurf.ai" 
                  className="text-sky-400 hover:text-sky-300 font-semibold text-lg"
                >
                  info@loadsurf.ai
                </a>
              </p>
            </Section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// Section component for consistent styling
const Section = ({ number, title, children }) => (
  <section className="mb-12">
    <h2 className="text-2xl font-bold text-white mb-6 flex items-baseline gap-3">
      <span className="text-sky-400">{number}.</span>
      {title}
    </h2>
    <div className="text-slate-300 space-y-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-2 [&>ul>li]:text-slate-400">
      {children}
    </div>
  </section>
);

// SubSection component for nested sections
const SubSection = ({ number, title, children }) => (
  <div className="mb-8 pl-4 border-l-2 border-slate-700">
    <h3 className="text-xl font-semibold text-white mb-4 flex items-baseline gap-2">
      <span className="text-sky-400">{number}</span>
      {title}
    </h3>
    <div className="text-slate-300 space-y-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-2 [&>ul>li]:text-slate-400">
      {children}
    </div>
  </div>
);

export default PrivacyPolicyPage;
